/**
 * MONSTER TOWN — THE HOUSE, AS DATA. OWNED BY THE INTERIORS CREW.
 *
 * A NEW ROOM IS A DATA DROP. Add an entry to `ROOMS` and you get: a scene, a
 * grid, a shell with cut-away walls, doors wired both ways, a starter set of
 * furniture the first time a kid walks in, its own floor and wallpaper, and a
 * save slot for all of it. No engine change, ever — that is the litmus test in
 * the brief and this file is where it is met.
 *
 * ROOM EXPANSION HOOK: `unlockAtLifetime` gates a room behind lifetime coins
 * earned. Every shipped room is 0 (LAW #2/#3 — nothing is locked away from a
 * 3-year-old on day one); the hook exists so a later drop can add "the attic"
 * without touching code.
 */

import type { Rot } from '../core/grid'
import type { PaletteName } from '../core/palette'
import type { PlaceableId, SceneId } from '../core/types'
import type { ExitPicture } from './decorArt'
import { F } from './ids'

export type Side = 'N' | 'E' | 'S' | 'W'

export interface RoomDoor {
  /** Which wall the doorway is cut into. */
  readonly side: Side
  /** Tile index along that wall (gx for N/S, gz for E/W). */
  readonly at: number
  /** Where it leads. `town` is the outside world. */
  readonly to: SceneId
}

export interface StarterPiece {
  readonly defId: PlaceableId
  readonly gx: number
  readonly gz: number
  readonly rot?: Rot
  /**
   * Optional colourway, from the def's own `tints`.
   *
   * ADDED ROUND 3. A white fridge against a pale mint wall has a silhouette a
   * three-year-old cannot find — the critic read it as "the kitchen fridge is
   * embedded in the back wall" when in fact it is a whole tile clear of it. The
   * fix for a merged silhouette is contrast, and contrast is data.
   */
  readonly tint?: PaletteName
}

export interface RoomDef {
  readonly id: SceneId
  /** Dev-only label. Never shown to a kid — LAW #4. */
  readonly devName: string
  /**
   * The PICTURE that goes on a doorway mat leading here. LAW #4 — a mat that
   * says nothing about where it goes is four identical mats in one room and a
   * one-in-four guess for a 3-year-old. Data, so a new room brings its own sign.
   */
  readonly picture: ExitPicture
  readonly width: number
  readonly depth: number
  readonly levels: number
  /** Index into FLOOR_STYLES / WALL_STYLES this room starts with. */
  readonly floor: number
  readonly wall: number
  readonly doors: readonly RoomDoor[]
  /** Where the monster stands after walking in. */
  readonly spawn: { readonly gx: number; readonly gz: number }
  /** Furnished ONCE, the first time this room is entered. Free pieces only. */
  readonly starter: readonly StarterPiece[]
  /** Lifetime coins needed before the room opens. 0 = day one. */
  readonly unlockAtLifetime: number
}

export const HOUSE_ID = 'house' as SceneId
export const KITCHEN_ID = 'house_kitchen' as SceneId
export const BEDROOM_ID = 'house_bedroom' as SceneId
export const BATHROOM_ID = 'house_bathroom' as SceneId
export const TOWN_ID = 'town' as SceneId

export const ROOMS: readonly RoomDef[] = [
  {
    id: HOUSE_ID,
    devName: 'Living room',
    picture: 'living',
    // Rooms are deliberately SMALL. A cosy room that a kid fills in five taps
    // beats a hall with furniture drifting in it — "world that feels empty" is
    // a named defect in the brief, and it applies indoors first.
    width: 9,
    depth: 7,
    levels: 3,
    floor: 0,
    wall: 0,
    spawn: { gx: 4, gz: 4 },
    doors: [
      { side: 'S', at: 4, to: TOWN_ID },
      { side: 'W', at: 3, to: KITCHEN_ID },
      { side: 'E', at: 3, to: BEDROOM_ID },
      { side: 'N', at: 4, to: BATHROOM_ID },
    ],
    /**
     * LAYOUT RULES, both learned the hard way.
     *
     * 1. KEEP THE AISLES CLEAR. A kid walks in and stops on cell (4,4). Under a
     *    fixed 45-degree iso the things drawn ON TOP of the monster are the ones
     *    with a larger x+z, so anything parked south-east of the spawn reads as
     *    "the monster is embedded in the table". Column gx=4 and row gz=3 carry
     *    the two aisles between the four doors and hold nothing but flat,
     *    walkable rugs.
     *
     * 2. NOTHING SOLID MAY TOUCH ANOTHER SOLID THING. Round 3: "chair seat
     *    intersects the table top... the salmon couch's back corner passes into
     *    the tall bookshelf". None of those pairs actually overlap in space —
     *    they are a tile apart — but at a 34-degree iso a chair back 1.16 tall
     *    standing one tile behind a table top projects straight through it, and
     *    two pieces in the same wood with coincident ink lines have no seam for
     *    the eye to find. The dining chairs are gone from the living room (a
     *    coffee table in front of the sofa needs none) and the one armchair sits
     *    on its own with a clear tile on every side.
     *
     * 3. ROT IS WHICH WAY A CHAIR FACES: rot 0 south, 1 west, 2 north, 3 east.
     *
     * ROUND 3 also fixed "80% empty brown floor... there is no bed in the
     * starter room". The brief's own deliverable sentence is "taps the bed and
     * it sleeps", so there is now a nap corner in the first room a kid walks
     * into, and two big walkable rugs break up the middle of the floor without
     * putting anything in the way of a three-year-old's finger.
     */
    starter: [
      // -- nap corner, north-west --------------------------------------------
      { defId: F.bed, gx: 0, gz: 0, rot: 0 },
      { defId: F.lampTable, gx: 2, gz: 0 },
      { defId: F.plant, gx: 2, gz: 2 },
      // -- sitting area, north-east ------------------------------------------
      { defId: F.couch, gx: 6, gz: 0, rot: 0 },
      // Directly in front of the couch, one row south: a coffee table, which is
      // the one place a low table can sit next to seating without a chair back
      // projecting through it.
      { defId: F.table, gx: 6, gz: 1 },
      { defId: F.lampTable, gx: 8, gz: 0 },
      // -- the middle: flat, walkable, and no longer bare --------------------
      { defId: F.rug, gx: 2, gz: 4, tint: 'floorRug' },
      { defId: F.rug, gx: 5, gz: 4, tint: 'wallMint' },
      // -- south-east: an armchair on its own, a tile clear all round --------
      { defId: F.chair, gx: 8, gz: 5, rot: 2 },
      // NOT (7,3): row gz=3 is the aisle between the kitchen and bedroom doors.
      { defId: F.plant, gx: 7, gz: 4 },
      // -- the two decorating toys, out of the aisles ------------------------
      { defId: F.paintCan, gx: 0, gz: 6 },
      { defId: F.floorSample, gx: 1, gz: 6 },
    ],
    unlockAtLifetime: 0,
  },
  {
    id: KITCHEN_ID,
    devName: 'Kitchen',
    picture: 'kitchen',
    width: 8,
    depth: 7,
    levels: 3,
    floor: 1,
    wall: 1,
    spawn: { gx: 4, gz: 3 },
    doors: [{ side: 'E', at: 3, to: HOUSE_ID }],
    // EVERY room gets a paint can and a floor sample. They are the only way to
    // change a room's walls and floor, they are free, and a kid who can only
    // repaint the living room would rightly wonder why.
    // Same rule as the living room: the aisle in from the door — cells (3..5,
    // 3..6) — stays empty, so nothing is ever drawn on top of the monster the
    // moment a kid walks in.
    starter: [
      // A WHITE FRIDGE ON A PALE MINT WALL HAS NO SILHOUETTE. It stands a whole
      // tile clear of the wall and still read as "embedded in the back wall";
      // the fix is contrast, not a move.
      { defId: F.fridge, gx: 1, gz: 1, tint: 'wallBlue' },
      { defId: F.stove, gx: 3, gz: 1 },
      { defId: F.sink, gx: 5, gz: 1 },
      { defId: F.table, gx: 1, gz: 4 },
      /*
       * DIAGONALLY OFF THE TABLE, NOT BUTTED AGAINST IT.
       *
       * At (0,4) this chair was in the table's own row: at a 34-degree iso its
       * 1.16-tall back projected straight through the 0.7-high table top and the
       * critic read it as "chair seat intersects the table top". One cell down
       * and across, the two silhouettes no longer share a screen column at all.
       * Faces NORTH (rot 2), back to the south wall, looking at the table.
       */
      { defId: F.chair, gx: 0, gz: 6, rot: 2 },
      { defId: F.plant, gx: 7, gz: 0 },
      // Walkable, so it fills the middle of the floor without ever standing in
      // the aisle a kid walks in along.
      { defId: F.rug, gx: 4, gz: 4, tint: 'wallMint' },
      { defId: F.paintCan, gx: 7, gz: 5 },
      { defId: F.floorSample, gx: 6, gz: 1 },
    ],
    unlockAtLifetime: 0,
  },
  {
    id: BEDROOM_ID,
    devName: 'Bedroom',
    picture: 'bed',
    width: 7,
    depth: 6,
    levels: 3,
    floor: 0,
    wall: 4,
    spawn: { gx: 3, gz: 2 },
    doors: [{ side: 'W', at: 3, to: HOUSE_ID }],
    starter: [
      { defId: F.bed, gx: 4, gz: 0, rot: 0 },
      { defId: F.lampTable, gx: 6, gz: 0 },
      { defId: F.couch, gx: 0, gz: 0, rot: 0 },
      { defId: F.plant, gx: 3, gz: 0 },
      // Two walkable rugs, same reason as the living room: they fill the floor
      // without ever standing between a three-year-old's finger and the ground.
      { defId: F.rug, gx: 1, gz: 3, tint: 'floorRug' },
      { defId: F.rug, gx: 4, gz: 3, tint: 'wallLilac' },
      { defId: F.chair, gx: 0, gz: 5, rot: 2 },
      { defId: F.paintCan, gx: 6, gz: 4 },
      { defId: F.floorSample, gx: 6, gz: 2 },
    ],
    unlockAtLifetime: 0,
  },
  {
    id: BATHROOM_ID,
    devName: 'Bathroom',
    picture: 'bath',
    width: 6,
    depth: 6,
    levels: 3,
    /*
     * CREAM TILES AND MINT STRIPES, NOT BLUE-GREY.
     *
     * This opened on floor 4 ("sky tiles", `wallpaperBlue`) under wall 2 ("blue
     * dots", `wallpaperBlue` again), which is the room the round-2 critic
     * photographed: "desaturated blue-grey with scattered diagonal dashes on the
     * wall that read as scratches, not tile", against a warm cream UI and a
     * mint-and-cream town. Two entries in one committed family fixes it — the
     * blue styles are still in the list for a kid to choose, they are just not
     * what the house opens on. (The dots pattern is what read as scratches; the
     * stripes read as tiling.)
     */
    floor: 1,
    wall: 1,
    // Two tiles clear of the bath. At (3,3) the monster walked in and stood
    // directly in front of the tub's near rim, and at a 45-degree iso "in front
    // of" and "inside" look the same — the critic read it as "the player monster
    // intersects the bath, white geometry passes through its head".
    spawn: { gx: 3, gz: 4 },
    doors: [{ side: 'S', at: 3, to: HOUSE_ID }],
    /*
     * "…only four objects in a large empty blue floor". A bath mat, a lamp and a
     * second plant, all free forever, all off the aisle in from the door.
     *
     * ROUND 3: THE BATH MAT NEVER ACTUALLY LANDED. It was a 2x2 rug anchored at
     * (1,3), which covers (1..2, 3..4) — and the floor sample placed two lines
     * ABOVE it already held (1,4), so `place()` rejected the rug every single
     * time and the room a kid saw had one fewer object than this list does. The
     * rug has moved to a clear 2x2, and both corner plants have come off the
     * corners, where their leaves projected over the wall tops.
     */
    starter: [
      { defId: F.bath, gx: 1, gz: 1, rot: 0 },
      { defId: F.sink, gx: 4, gz: 1 },
      { defId: F.plant, gx: 5, gz: 2 },
      { defId: F.plant, gx: 0, gz: 1 },
      { defId: F.rug, gx: 2, gz: 3, tint: 'wallBlue' },
      { defId: F.lampTable, gx: 5, gz: 4 },
      { defId: F.paintCan, gx: 0, gz: 4 },
      { defId: F.floorSample, gx: 1, gz: 4 },
    ],
    unlockAtLifetime: 0,
  },
]

export function roomDef(id: SceneId): RoomDef | undefined {
  return ROOMS.find((r) => r.id === id)
}

export function isRoom(id: SceneId | null): boolean {
  return id !== null && ROOMS.some((r) => r.id === id)
}

/**
 * The floor cell a kid stands on to use a doorway. The wall itself sits just
 * outside the grid, so a doorway never eats a buildable tile.
 */
export function doorCell(room: RoomDef, door: RoomDoor): { gx: number; gy: number; gz: number } {
  switch (door.side) {
    case 'N':
      return { gx: door.at, gy: 0, gz: 0 }
    case 'S':
      return { gx: door.at, gy: 0, gz: room.depth - 1 }
    case 'W':
      return { gx: 0, gy: 0, gz: door.at }
    case 'E':
      return { gx: room.width - 1, gy: 0, gz: door.at }
  }
}

/**
 * WHERE THE FRONT DOOR STANDS IN THE TOWN.
 *
 * The world crew owns the house exterior; until their shell lands this is a
 * standalone porch two tiles north of the plot centre, right where the camera
 * starts. When their house arrives they call `attachHouseEntrance()` with their
 * own doorstep cell and this constant stops being used — one call, no merge.
 */
export const TOWN_DOOR_CELL = { gx: 12, gy: 0, gz: 10 } as const
